import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircleIcon, HomeIcon, SearchIcon, ArrowLeftIcon } from 'lucide-react';
import { usePageMeta } from '../hooks/usePageMeta';
interface PartnerBranding {
  name: string;
  logo: string;
  primaryColor: string;
  accentColor: string;
  backUrl: string;
  programTitle?: string;
}
export function ThankYouPage() {
  usePageMeta('Thank You | FindOnlineColleges', 'Your information request has been submitted. A school representative will be in touch shortly.', {
    ogImage: 'https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=1200&h=630&fit=crop&q=80',
    ogType: 'website'
  });
  const navigate = useNavigate();
  const location = useLocation();
  const formData = (location.state as any)?.formData;
  const partner = (location.state as any)?.partner as PartnerBranding | undefined;
  if (partner) {
    return <main className="min-h-screen flex flex-col" style={{
      backgroundColor: '#F8FAFB'
    }} data-id="element-2758" data-mp="1no9cjus6dxwi">
        {/* Partner Header */}
        <header className="bg-white py-4 px-6 sm:px-12 flex items-center justify-center shadow-sm border-b border-gray-100" data-id="element-2759" data-mp="3mqt1d17w0uvl">
          {partner.logo ? <img src={partner.logo} alt={`${partner.name} Logo`} className="h-14 object-contain" data-id="element-2760" data-mp="3vs9a74j20oxp" /> : <span className="text-xl font-bold" style={{
          color: partner.primaryColor
        }} data-id="element-2761" data-mp="4ewh5c323cph">
              {partner.name}
            </span>}
        </header>

        {/* Hero */}
        <div className="relative overflow-hidden py-16 md:py-24" style={{
        background: `linear-gradient(135deg, ${partner.primaryColor} 0%, ${partner.primaryColor}dd 60%, ${partner.accentColor}88 100%)`
      }} data-id="element-2762" data-mp="ecavn65f5rf5">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10" data-id="element-2763" data-mp="nch7knlgyjc7">
            <motion.div initial={{
            scale: 0,
            opacity: 0
          }} animate={{
            scale: 1,
            opacity: 1
          }} transition={{
            type: 'spring',
            stiffness: 200,
            damping: 20,
            delay: 0.1
          }} className="mx-auto mb-6" data-id="element-2764" data-mp="2l4bq0cfyw83b">
              <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto border-2" style={{
              backgroundColor: `${partner.accentColor}20`,
              borderColor: `${partner.accentColor}40`
            }} data-id="element-2765" data-mp="3ni983gwdtacw">
                <CheckCircleIcon className="w-10 h-10" style={{
                color: partner.accentColor
              }} data-id="element-2766" data-mp="f99c5lzopx8x" />
              </div>
            </motion.div>

            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.25
          }} className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-2767" data-mp="a9wgqcuqzhuq">
              Thank You{formData?.firstName ? `, ${formData.firstName}` : ''}!
            </motion.h1>

            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.35
          }} className="text-lg text-white/90 mb-2" data-id="element-2768" data-mp="t1p7s1q9aszt">
              Your information has been submitted to{' '}
              <strong className="text-white" data-id="element-2769" data-mp="13kzoj556qbp2">{partner.name}</strong>.
            </motion.p>

            {partner.programTitle && <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.4
          }} className="text-base text-white/70" data-id="element-2770" data-mp="17ls1o9h6h0qm">
                Program: {partner.programTitle}
              </motion.p>}

            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.45
          }} className="text-base text-white/70 max-w-md mx-auto mt-3" data-id="element-2771" data-mp="jvpbjywtq4un">
              A representative from {partner.name} will reach out to you shortly
              to discuss your education options.
            </motion.p>
          </div>
        </div>

        {/* Steps Card */}
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 relative z-20 pb-16 w-full" data-id="element-2772" data-mp="2xpiubr2o1xj">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.55
        }} className="bg-white rounded-xl shadow-lg p-8" data-id="element-2773" data-mp="3u9r29nqy1c6q">
            <h2 className="text-lg font-bold mb-4" style={{
            color: partner.primaryColor
          }} data-id="element-2774" data-mp="3l8147z2k3ezz">
              What Happens Next?
            </h2>
            <div className="space-y-4" data-id="element-2775" data-mp="3bcpyg694li4d">
              <div className="flex items-start gap-4" data-id="element-2776" data-mp="37am9cetjaf0m">
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{
                backgroundColor: `${partner.primaryColor}15`
              }} data-id="element-2777" data-mp="37bxkx0t80r2a">
                  <span className="text-sm font-bold" style={{
                  color: partner.primaryColor
                }} data-id="element-2778" data-mp="15kkjwmudj3bi">
                    1
                  </span>
                </div>
                <div data-id="element-2779" data-mp="2seo3ttjhejr5">
                  <p className="font-semibold text-gray-800" data-id="element-2780" data-mp="2se4j652g5h9f">
                    Application Review
                  </p>
                  <p className="text-sm text-gray-600" data-id="element-2781" data-mp="3189ilcvnc10k">
                    {partner.name} will review your information and match you
                    with the best program options.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4" data-id="element-2782" data-mp="29hwdj05iv4dl">
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{
                backgroundColor: `${partner.primaryColor}15`
              }} data-id="element-2783" data-mp="2dioqa3dlrrem">
                  <span className="text-sm font-bold" style={{
                  color: partner.primaryColor
                }} data-id="element-2784" data-mp="3oxvgzy472c7i">
                    2
                  </span>
                </div>
                <div data-id="element-2785" data-mp="1tuozkvg1ynfn">
                  <p className="font-semibold text-gray-800" data-id="element-2786" data-mp="1tu5re6g7fdet">
                    Expect a Call or Email
                  </p>
                  <p className="text-sm text-gray-600" data-id="element-2787" data-mp="1psbwyfwlr5n3">
                    An admissions advisor from {partner.name} will contact you
                    {formData?.email ? ` at ${formData.email}` : ''} to discuss
                    next steps.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4" data-id="element-2788" data-mp="1b4qrwgg62wcj">
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{
                backgroundColor: `${partner.primaryColor}15`
              }} data-id="element-2789" data-mp="165mltyojheqf">
                  <span className="text-sm font-bold" style={{
                  color: partner.primaryColor
                }} data-id="element-2790" data-mp="1g2scx6x5l5qn">
                    3
                  </span>
                </div>
                <div data-id="element-2791" data-mp="newlagmfy40q">
                  <p className="font-semibold text-gray-800" data-id="element-2792" data-mp="ndlgcdh9eq81">
                    Enroll & Get Started
                  </p>
                  <p className="text-sm text-gray-600" data-id="element-2793" data-mp="2z0f3jm5wqlmr">
                    Once you're ready, your advisor at {partner.name} will guide
                    you through the enrollment process.
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-3" data-id="element-2794" data-mp="2u3x5f5531lje">
              <button onClick={() => navigate(partner.backUrl)} className="flex items-center justify-center gap-2 px-6 py-3 text-white font-semibold rounded-md transition-colors text-sm" style={{
              backgroundColor: partner.primaryColor
            }} onMouseEnter={e => e.currentTarget.style.opacity = '0.9'} onMouseLeave={e => e.currentTarget.style.opacity = '1'} data-id="element-2795" data-mp="3hspt6ebjwn2x">
                <ArrowLeftIcon className="w-4 h-4" data-id="element-2796" data-mp="3dspecskji9vc" />
                Back to {partner.name}
              </button>
              <button onClick={() => navigate('/')} className="flex items-center justify-center gap-2 px-6 py-3 border font-semibold rounded-md transition-colors text-sm" style={{
              borderColor: partner.primaryColor,
              color: partner.primaryColor
            }} data-id="element-2797" data-mp="22bpos4pku950">
                <HomeIcon className="w-4 h-4" data-id="element-2798" data-mp="2p2k4ysd668ap" />
                Explore More Schools
              </button>
            </div>
          </motion.div>
        </div>
      </main>;
  }
  // Default generic thank you page
  return <main className="flex-grow bg-light" data-id="element-2799" data-mp="3cpijvlln586i">
      <div className="hero-bg relative overflow-hidden py-16 md:py-24" data-id="element-2800" data-mp="3cogx4jv9ix8y">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10" data-id="element-2801" data-mp="37qfchui6upvd">
          <motion.div initial={{
          scale: 0,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} transition={{
          type: 'spring',
          stiffness: 200,
          damping: 20,
          delay: 0.1
        }} className="mx-auto mb-6" data-id="element-2802" data-mp="32zff6o55q5sc">
            <div className="w-20 h-20 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto border border-white/20" data-id="element-2803" data-mp="3qp1edblj4l0c">
              <CheckCircleIcon className="w-10 h-10 text-gold" data-id="element-2804" data-mp="1wv215qweofy" />
            </div>
          </motion.div>

          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.25
        }} className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-2805" data-mp="6un0kim1t8t4">
            Thank You{formData?.firstName ? `, ${formData.firstName}` : ''}!
          </motion.h1>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.35
        }} className="text-lg text-gray-300 mb-2" data-id="element-2806" data-mp="psqy0fskpjf3">
            Your information has been submitted successfully.
          </motion.p>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.45
        }} className="text-base text-gray-400 max-w-md mx-auto" data-id="element-2807" data-mp="14iy7w74ilu46">
            A representative from one of our partner schools will reach out to
            you shortly to discuss your education options.
          </motion.p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 relative z-20 pb-16" data-id="element-2808" data-mp="52k6bgn8kbl7">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.55
      }} className="bg-white rounded-xl shadow-lg p-8" data-id="element-2809" data-mp="9v3pct4ozyxe">
          <h2 className="text-lg font-bold text-navy mb-4" data-id="element-2810" data-mp="it5rjc05wxm9">
            What Happens Next?
          </h2>
          <div className="space-y-4" data-id="element-2811" data-mp="numm6ps1znue">
            <div className="flex items-start gap-4" data-id="element-2812" data-mp="sso59aphbn5v">
              <div className="w-8 h-8 rounded-full bg-navy/10 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-2813" data-mp="ss4zsth0mr8q">
                <span className="text-sm font-bold text-navy" data-id="element-2814" data-mp="2pnj8j8reyctt">1</span>
              </div>
              <div data-id="element-2815" data-mp="2umdp041wrpar">
                <p className="font-semibold text-gray-800" data-id="element-2816" data-mp="2n0rz823aloar">
                  Review Your Matches
                </p>
                <p className="text-sm text-gray-600" data-id="element-2817" data-mp="2i9icpad1j133">
                  Our system is matching you with accredited programs that fit
                  your goals and preferences.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4" data-id="element-2818" data-mp="28d6vc5ylfl1s">
              <div className="w-8 h-8 rounded-full bg-navy/10 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-2819" data-mp="35zcorppjclma">
                <span className="text-sm font-bold text-navy" data-id="element-2820" data-mp="3109t2vvg3rqz">2</span>
              </div>
              <div data-id="element-2821" data-mp="2w0x7eegb8qpz">
                <p className="font-semibold text-gray-800" data-id="element-2822" data-mp="2w0f4iyz57e93">
                  Expect a Call or Email
                </p>
                <p className="text-sm text-gray-600" data-id="element-2823" data-mp="2r1tz8l51a4ds">
                  An admissions advisor from a matched school will contact you
                  {formData?.email ? ` at ${formData.email}` : ''} to discuss
                  next steps.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4" data-id="element-2824" data-mp="7o9brl69ambk">
              <div className="w-8 h-8 rounded-full bg-navy/10 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-2825" data-mp="bp34qsyk2rbb">
                <span className="text-sm font-bold text-navy" data-id="element-2826" data-mp="3tvr7wda1p4ec">3</span>
              </div>
              <div data-id="element-2827" data-mp="2o53ujjeyzwy">
                <p className="font-semibold text-gray-800" data-id="element-2828" data-mp="2ndazgh7bgad">
                  Enroll & Get Started
                </p>
                <p className="text-sm text-gray-600" data-id="element-2829" data-mp="3k3v5wr4u8u4u">
                  Once you've found the right fit, your advisor will guide you
                  through the enrollment process.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-8 flex flex-col sm:flex-row gap-3" data-id="element-2830" data-mp="3g31irzz7zjd0">
            <button onClick={() => navigate('/')} className="flex items-center justify-center gap-2 px-6 py-3 bg-navy hover:bg-navy-light text-white font-semibold rounded-md transition-colors text-sm" data-id="element-2831" data-mp="3g16ucboqp946">
              <HomeIcon className="w-4 h-4" data-id="element-2832" data-mp="3677zzyuwqtms" />
              Back to Home
            </button>
            <button onClick={() => navigate('/colleges')} className="flex items-center justify-center gap-2 px-6 py-3 border border-navy text-navy hover:bg-navy/5 font-semibold rounded-md transition-colors text-sm" data-id="element-2833" data-mp="2mleqj45wqep0">
              <SearchIcon className="w-4 h-4" data-id="element-2834" data-mp="2riy4e2yp0pgf" />
              Browse More Schools
            </button>
          </div>
        </motion.div>
      </div>
    </main>;
}